import React from 'react';
import { Field, useFormikContext } from 'formik';

import { AutoCompleteSelectUI, DatePickerUI, DateTimePickerUI, FormErrorUI, FormLabelUI, InputUI } from '../Interface';
import dayjs from 'dayjs';
import { getProjectOrderNumberName } from '../../utils/getProjectOrderNumberName';
import useParam from '../../context/ParamContext';
import { TaskResponsibleTypeEnum } from '../../config/taskList';

const ProjectTaskFields = ({ autocompleteOptions, viewField, autocompleteData, selectedValues }) => {
  const { values, validateField, setFieldValue, errors } = useFormikContext();
  const { params } = useParam();

  return (
    <div className="grid grid-cols-4 gap-x-4 gap-y-4 mx-4 mb-5">
      <div className="col-span-4">
        <Field
          name="name"
          as={InputUI}
          fullWidth
          label={<FormLabelUI text="Megnevezés" />}
          variant="standard"
          helperText={<FormErrorUI message={errors.name} />}
          InputLabelProps={{ shrink: values.name && values.name !== '' }}
          disabled={viewField}
        />
      </div>
      {params.TASKLIST_SHOW_TASKRATIO_FIELD === 'Y' && (
        <div className="md:col-span-1 col-span-4">
          <Field
            type="number"
            name="taskRatio"
            as={InputUI}
            fullWidth
            label={<FormLabelUI text="Feladat arány" />}
            variant="standard"
            helperText={<FormErrorUI message={errors.taskRatio} />}
            InputLabelProps={{ shrink: values.taskRatio && values.taskRatio !== '' }}
            disabled={viewField}
          />
        </div>
      )}
      <div className="col-span-4 md:col-span-2">
        <Field
          type="autocomplete"
          name="lineId"
          component={AutoCompleteSelectUI}
          fullWidth
          label={<FormLabelUI text="Projekt" />}
          variant="standard"
          helperText={<FormErrorUI message={errors.lineId} />}
          onChange={(_e, newVal) => {
            const findedProject = autocompleteData?.project.find((item) => item.projectId === newVal?.value);
            if (findedProject) {
              setFieldValue('projectOrderNumber', findedProject.orderNumber || '');
            } else {
              setFieldValue('projectOrderNumber', '');
            }

            setFieldValue('lineId', newVal?.value ?? null).then(() => {
              validateField('lineId');
            });
          }}
          InputLabelProps={{ shrink: values.lineId !== '' }}
          selectedValue={values.lineId}
          dataset={autocompleteOptions.project}
          disabled={viewField}
        />
      </div>
      <div className="col-span-4 md:col-span-1">
        <Field
          type="autocomplete"
          name="projectOrderNumber"
          component={InputUI}
          fullWidth
          label={<FormLabelUI text={getProjectOrderNumberName(params)} />}
          variant="standard"
          helperText={<FormErrorUI message={errors.projectOrderNumber} />}
          disabled={true}
          InputLabelProps={{ shrink: values.projectOrderNumber !== '' }}
          value={values.projectOrderNumber}
        />
      </div>
      <div className="col-span-4 md:col-span-1">
        <Field
          type="autocomplete"
          name="typeId"
          component={AutoCompleteSelectUI}
          fullWidth
          label={<FormLabelUI text="Típus" />}
          variant="standard"
          helperText={<FormErrorUI message={errors.typeId} />}
          onChange={(_e, newVal) => {
            setFieldValue('typeId', newVal?.value ?? null).then(() => {
              validateField('typeId');
            });
          }}
          InputLabelProps={{ shrink: values.typeId !== '' }}
          selectedValue={values.typeId}
          dataset={autocompleteOptions.type}
          disabled={viewField}
        />
      </div>
      <div className="col-span-4 md:col-span-1">
        <Field
          type="autocomplete"
          name="statusId"
          component={AutoCompleteSelectUI}
          fullWidth
          label={<FormLabelUI text="Státusz" />}
          variant="standard"
          helperText={<FormErrorUI message={errors.statusId} />}
          onChange={(_e, newVal) => {
            const findedStatus = autocompleteData?.status?.statusItems.find((item) => item.itemId === newVal?.value);
            if (findedStatus) {
              setFieldValue('preparedness', findedStatus.preparedness ?? null);
            } else {
              setFieldValue('preparedness', null);
            }

            setFieldValue('statusId', newVal?.value ?? null).then(() => {
              validateField('statusId');
            });
          }}
          InputLabelProps={{ shrink: values.statusId !== '' }}
          selectedValue={values.statusId}
          dataset={autocompleteOptions.status}
          disabled={viewField}
        />
      </div>
      <div className="md:col-span-1 col-span-4">
        <Field
          type="number"
          name="preparedness"
          as={InputUI}
          fullWidth
          label={<FormLabelUI text="Készültség" />}
          variant="standard"
          helperText={<FormErrorUI message={errors.preparedness} />}
          InputLabelProps={{ shrink: values.preparedness && values.preparedness !== '' }}
          disabled={viewField}
        />
      </div>
      <div className="col-span-4 md:col-span-1">
        <Field
          type="date"
          name="startDate"
          component={DatePickerUI}
          value={dayjs(values.startDate)}
          fullWidth
          label={<FormLabelUI text="Kezdő dátum" />}
          variant="standard"
          helperText={<FormErrorUI message={errors.startDate} />}
          onChange={(date) => {
            setFieldValue('startDate', date).then(() => {
              validateField('startDate');
            });
          }}
          InputLabelProps={{ shrink: values.startDate !== '' }}
          disabled={viewField}
        />
      </div>
      <div className="md:col-span-1 col-span-4">
        <Field
          type="number"
          name="duration"
          as={InputUI}
          fullWidth
          label={<FormLabelUI text="Időintervallum (nap)" />}
          variant="standard"
          helperText={<FormErrorUI message={errors.duration} />}
          InputLabelProps={{ shrink: values.duration && values.duration !== '' }}
          disabled={viewField}
        />
      </div>
      <div className="col-span-4 md:col-span-1">
        <Field
          type="autocomplete"
          name="responsiblePersonId"
          component={AutoCompleteSelectUI}
          fullWidth
          label={<FormLabelUI text="Felelős" />}
          variant="standard"
          helperText={<FormErrorUI message={errors.responsiblePersonId} />}
          onChange={(_e, newVal) => {
            const type = newVal?.type || TaskResponsibleTypeEnum.USER;
            const value = newVal?.value || null;

            setFieldValue('responsibleType', type);

            // reset
            setFieldValue('responsiblePersonId', null);
            setFieldValue('responsibleUserGroupId', null);
            setFieldValue('responsibleCompanyId', null);
            setFieldValue('responsibleWorkerId', null);

            if (!value) return;

            const fieldByType = {
              [TaskResponsibleTypeEnum.USER]: 'responsiblePersonId',
              [TaskResponsibleTypeEnum.USERGROUP]: 'responsibleUserGroupId',
              [TaskResponsibleTypeEnum.BUSINESSPARTNER]: 'responsibleCompanyId',
              [TaskResponsibleTypeEnum.WORKER]: 'responsibleWorkerId',
            };

            const field = fieldByType[type];
            if (!field) return;

            setFieldValue(field, value).then(() => {
              if (field === 'responsiblePersonId') {
                validateField('responsiblePersonId');
              }
            });
          }}
          InputLabelProps={{
            shrink:
              values.responsiblePersonId !== '' ??
              values.responsibleUserGroupId !== '' ??
              values.responsibleCompanyId !== '' ??
              values.responsibleWorkerId !== '',
          }}
          selectedValue={
            values.responsiblePersonId ??
            values.responsibleUserGroupId ??
            values.responsibleCompanyId ??
            values.responsibleWorkerId
          }
          selectedLabelValue={
            autocompleteOptions?.responsible?.find(
              (r) =>
                r.value ===
                  (values.responsiblePersonId ??
                    values.responsibleUserGroupId ??
                    values.responsibleCompanyId ??
                    values.responsibleWorkerId) && r.type === values.responsibleType
            )?.label
          }
          dataset={autocompleteOptions.responsible}
          disabled={values.userGroupId || viewField}
        />
      </div>
      <div className="col-span-4 md:col-span-1">
        <Field
          type="autocomplete"
          name="userGroupId"
          component={AutoCompleteSelectUI}
          fullWidth
          label={<FormLabelUI text="Felelősök (csoport)" />}
          variant="standard"
          helperText={<FormErrorUI message={errors.userGroupId} />}
          onChange={(_e, newVal) => {
            setFieldValue('userGroupId', newVal?.value ?? null).then(() => {
              validateField('userGroupId');
            });
          }}
          InputLabelProps={{ shrink: values.userGroupId !== '' }}
          selectedValue={values.userGroupId}
          dataset={autocompleteOptions.userGroup}
          disabled={values.responsiblePersonId || viewField}
        />
      </div>
      <div className="col-span-4 md:col-span-1">
        <Field
          type="autocomplete"
          name="userId"
          component={AutoCompleteSelectUI}
          fullWidth
          label={<FormLabelUI text="Tulajdonos" />}
          variant="standard"
          helperText={<FormErrorUI message={errors.userId} />}
          onChange={(_e, newVal) => {
            setFieldValue('userId', newVal?.value ?? null).then(() => {
              validateField('userId');
            });
          }}
          InputLabelProps={{ shrink: values.userId !== '' }}
          selectedValue={values.userId}
          selectedLabelValue={selectedValues?.user?.nickName}
          dataset={autocompleteOptions.user}
          disabled={viewField}
        />
      </div>
      <div className="col-span-4 md:col-span-1">
        <Field
          type="dateTime"
          name="deadLine"
          component={DateTimePickerUI}
          value={dayjs(values.deadLine)}
          fullWidth
          label={<FormLabelUI text="Határidő" />}
          variant="standard"
          helperText={<FormErrorUI message={errors.deadLine} />}
          onChange={(date) => {
            setFieldValue('deadLine', date).then(() => {
              validateField('deadLine');
            });
          }}
          InputLabelProps={{ shrink: values.deadLine !== '' }}
          disabled={viewField}
        />
      </div>
    </div>
  );
};

export default ProjectTaskFields;
